<script>
import { onMounted, ref, inject } from "vue";
import { useStore } from "vuex";
import { fetchById as fetchPrescriptionTest } from "@/use/repositories/prescriptionTests/fetchById";
import { update as updateTestWorkQueue } from '@/use/repositories/testWorkQueue/update';
import { useInjectParentAndSortByState } from '@/composables/useInjectParentAndSortByState';
import Spinner from "@/components/general/Spinner";
import PhaseState from "@/components/History/PhaseState.vue";
import AddIsoPhase from "@/components/Consulting/AddIsoPhase.vue"

import _last from "lodash/last"

export default {
  components: { Spinner, PhaseState, AddIsoPhase },
  props: {
    prescriptionTestId: {
      type: Number,
      required: true,
    },
    isCurrent: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const store = useStore();
    const { states, roles } = inject('constants');
    const readonly = inject('readonly');
    const { testWorkQueuePhases, structuredPhases, pushIsoPhase, isCurrentPhase } = useInjectParentAndSortByState();
    const showSpinner = ref(false);
    const relativeTest = ref(null);

    const isTwqPhaseAddable = (twqPhase) => {
      return (
        (! twqPhase.parent && twqPhase.state.slug === states.PENDING) ||
        (twqPhase.parent?.slug === states.COMPLETED &&
          twqPhase.state.slug === states.PENDING &&
          store.state.user.roles.includes(roles.MANAGER))
      );
    };

    const areAllPhasesCompleted = () => {
      return (
        testWorkQueuePhases.value.length &&
        testWorkQueuePhases.value.every(item => item.state.slug === states.COMPLETED)
      );
    };

    const canAddIsoPhase = ({ twqPhase = null, checkAllPhases = false } = {}) => {
      if (readonly) return false;
      if (twqPhase) return isTwqPhaseAddable(twqPhase);
      if (checkAllPhases) {
        return (
          props.isCurrent &&
          areAllPhasesCompleted() &&
          relativeTest.value?.state?.slug === 'in-lavorazione'
        );
      }

      return false;
    };

    const updateIsoPhase = (isoPhase, allPhasesComnpleted) => {
      pushIsoPhase(isoPhase, allPhasesComnpleted);

      if (allPhasesComnpleted) {
        updateTestWorkQueue({
          id: isoPhase?.test_work_queue_id,
          state_slug: states.IN_THE_WORKS,
          action: "update_state",
        });
      }
    };

    const fetchHistoryStates = async () => {
      showSpinner.value = true;
      try {
        const { prescriptionTest } = await fetchPrescriptionTest(props.prescriptionTestId, {
          action: "with_all_relations_and_states",
        });
        relativeTest.value = prescriptionTest;
        testWorkQueuePhases.value = prescriptionTest.test_work_queue.test_work_queue_phases;
      } catch (error) {
        console.error(error);
      } finally {
        showSpinner.value = false;
      }
    };

    const isLastAssignedState = (state, index) => state.slug === states.ASSIGNED && index === 0;

    const getClassFromSlug = (slug) => {
      switch (slug) {
        case states.COMPLETED:
          return 'bg-blue';
        default:
          return 'btn-red';
      }
    }
    const lastElementOf = (array) => _last(array);

    onMounted(async () => await fetchHistoryStates());

    return {
      states,
      showSpinner,
      structuredPhases,
      canAddIsoPhase,
      updateIsoPhase,
      lastElementOf,
      readonly,
      relativeTest,
      isCurrentPhase,
      isLastAssignedState,
      getClassFromSlug
    };
  },
};
</script>
<template>
  <Spinner v-if="showSpinner" />
  <template v-else-if="structuredPhases && structuredPhases.length">
      <AddIsoPhase class="py-4"
        v-if="canAddIsoPhase({ checkAllPhases: true })"
        @push-iso-phase="updateIsoPhase($event, true)"
        :twqPhase="lastElementOf(structuredPhases)" />
    <div
      v-for="(phase, index) in structuredPhases.reverse()"
      :key="index"
      :class="{ 'current-internal-phase': isCurrentPhase(phase) }"
      >
      <h5 class="text-violet">{{ phase.internal_phase.name }}</h5>
      <ul class="custom-list">
        <PhaseState
          v-for="(state, index) in phase.states.reverse()"
          :key="index"
          :phase="phase"
          :state="state"
          :with-user="isLastAssignedState(state, index) && isCurrentPhase(phase)" />
      </ul>
      <AddIsoPhase class="py-4"
        v-if="canAddIsoPhase({ twqPhase: phase })"
        @push-iso-phase="updateIsoPhase($event, false)"
        :twqPhase="phase" />
    </div>
  </template>
  <p v-else>Nessuno stato</p>
</template>

<style scoped lang="scss">
@import "@/scss/abstracts/_variables.scss";

  .current-internal-phase {
    border-left: 5px solid $violet;
    padding-left: 0.5rem;
    margin-left: -10px;
  }

  .csustom-lit {
    list-style: none;
  }

  .custom-list li {
    position: relative;
    padding-left: 2rem;
  }

  .custom-list li::before {
    content: "•";
    color: #725D82;
    font-size: 2rem;
    position: absolute;
    left: 0;
    top: 16%;
    transform: translateY(-50%);
  }
</style>
